import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Users2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { PageHeader } from '@/components/shared/PageHeader';
import { SaveStatus, useSaveStatus } from '@/components/shared/SaveStatus';
import { useAuth } from '@/contexts/AuthContext';
import { useDraft } from '@/hooks/useDraft';
import { teamService, type Invitation, type Member, type Role } from '@/services/teamService';
import { ApiError } from '@/lib/api';
import { formatDate } from '@/lib/formatDate';

type TeamData = {
  members: Member[];
  pending_invitations: Invitation[];
} | null;

export function TeamPage() {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();
  const [data, setData] = useState<TeamData>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isInviteOpen, setInviteOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<Invitation | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionNotice, setActionNotice] = useState<string | null>(null);
  // F099/A18 — role change had no feedback; surface saving/saved/error inline.
  const roleSave = useSaveStatus();

  const fetchData = useCallback(async () => {
    setLoadError(null);
    try {
      const result = await teamService.listMembers();
      setData(result);
    } catch {
      setLoadError(t('team.error_generic'));
    }
  }, [t]);

  useEffect(() => {
    if (!isAdmin) return;
    void fetchData();
  }, [isAdmin, fetchData]);

  // F066 #35: action feedback is ephemeral — cleared at the start of EVERY
  // new action so a stale error can't make a later success look like failure.
  const clearFeedback = () => {
    setActionError(null);
    setActionNotice(null);
  };

  // F066 #34: the backend protects the last admin with a 409, but the owner
  // should never get that far — disable the affordances up front. The 409
  // handling below stays as defense-in-depth for the concurrent-admins race.
  const adminCount = data?.members.filter((m) => m.role === 'admin').length ?? 0;
  const isLastAdmin = (member: Member) => member.role === 'admin' && adminCount === 1;

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    clearFeedback();
    try {
      await teamService.removeMember(removeTarget.id);
      setRemoveTarget(null);
      await fetchData();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'last_admin') {
        setActionError(t('team.error_last_admin'));
      } else {
        setActionError(t('team.error_generic'));
      }
      setRemoveTarget(null);
    }
  };

  const handleRevokeConfirm = async () => {
    if (!revokeTarget) return;
    clearFeedback();
    try {
      await teamService.revokeInvite(revokeTarget.id);
      setRevokeTarget(null);
      await fetchData();
    } catch {
      setActionError(t('team.error_generic'));
      setRevokeTarget(null);
    }
  };

  const handleRoleChange = async (member: Member, newRole: Role) => {
    clearFeedback();
    await roleSave.save(async () => {
      await teamService.changeRole(member.id, newRole);
      await fetchData();
    });
  };

  // F099/A18 — map a role-save failure to a friendly inline message; the
  // last-admin guard keeps its specific copy, everything else stays generic.
  const roleErrorMessage = (err: unknown): string =>
    err instanceof ApiError && err.code === 'last_admin'
      ? t('team.error_last_admin')
      : t('team.error_generic');

  if (!isAdmin) {
    return (
      <div className="flex flex-col gap-6 p-6">
        <PageHeader title={t('team.title')} subtitle={t('team.subtitle')} />
        <EmptyState
          icon={Users2}
          title={t('team.admin_only_title')}
          subtitle={t('team.admin_only_subtitle')}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <PageHeader
        title={t('team.title')}
        subtitle={t('team.subtitle')}
        actions={
          <Button
            type="button"
            data-testid="team-invite-button"
            onClick={() => {
              clearFeedback();
              setInviteOpen(true);
            }}
          >
            {t('team.invite_button')}
          </Button>
        }
      />

      {loadError && (
        <div role="alert" className="text-sm text-destructive">
          {loadError}
        </div>
      )}

      {/* Action feedback — rendered next to its origin (the members/invites
          sections), dismissible, and cleared on every new action (F066 #35). */}
      {actionError && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-lg border border-destructive/50 px-3 py-1.5 text-sm text-destructive"
        >
          <span>{actionError}</span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="action-error-dismiss"
            aria-label={t('common.dismiss')}
            className="min-h-11 shrink-0"
            onClick={() => setActionError(null)}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      {actionNotice && (
        <div
          role="status"
          className="flex items-center justify-between gap-3 rounded-lg border px-3 py-1.5 text-sm"
        >
          <span className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" />
            {actionNotice}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            data-testid="action-notice-dismiss"
            aria-label={t('common.dismiss')}
            className="min-h-11 shrink-0"
            onClick={() => setActionNotice(null)}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      )}

      {/* Members section */}
      <section aria-label={t('team.members_title')}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold">{t('team.members_title')}</h2>
          <SaveStatus
            status={roleSave.status}
            errorMessage={roleSave.error ? roleErrorMessage(roleSave.error) : undefined}
            onRetry={() => void roleSave.retry()}
          />
        </div>

        {data === null && !loadError ? (
          <div className="flex flex-col gap-2" data-testid="team-skeleton">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : data !== null && data.members.length === 0 ? (
          <EmptyState
            icon={Users2}
            title={t('team.empty_title')}
            subtitle={t('team.empty_subtitle')}
            cta={
              <Button
                type="button"
                data-testid="team-empty-cta"
                onClick={() => {
                  clearFeedback();
                  setInviteOpen(true);
                }}
              >
                {t('team.invite_button')}
              </Button>
            }
          />
        ) : data !== null ? (
          <>
            {/* Desktop table — §21.7 */}
            <div data-testid="team-members-desktop" className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('team.email_label')}</TableHead>
                    <TableHead>{t('team.role_label')}</TableHead>
                    <TableHead>{t('team.last_login')}</TableHead>
                    <TableHead className="text-right">{''}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-medium">
                        {/* cap + truncate so an unbreakable email can't inflate
                            the column past the wrapper (§21.7) */}
                        <div className="max-w-64 truncate">{member.name}</div>
                        <div className="max-w-64 truncate text-xs text-muted-foreground">
                          {member.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <select
                          value={member.role}
                          aria-label={t('team.role_aria', { name: member.name })}
                          data-testid={`member-role-select-${member.id}`}
                          disabled={isLastAdmin(member)}
                          className="rounded-md border bg-background px-2 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                          onChange={(e) => {
                            void handleRoleChange(member, e.target.value as Role);
                          }}
                        >
                          <option value="admin">{t('team.role_admin')}</option>
                          <option value="member">{t('team.role_member')}</option>
                        </select>
                        {isLastAdmin(member) && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            {t('team.last_admin_hint')}
                          </p>
                        )}
                      </TableCell>
                      <TableCell>
                        {member.last_login_at ? formatDate(member.last_login_at) : t('team.never')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-label={t('team.remove_aria', { name: member.name })}
                          data-testid={`member-remove-${member.id}`}
                          disabled={isLastAdmin(member)}
                          className="min-h-11"
                          onClick={() => {
                            clearFeedback();
                            setRemoveTarget(member);
                          }}
                        >
                          {t('team.remove_button')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Mobile cards — §21.7 */}
            <div data-testid="team-members-mobile" className="flex flex-col gap-3 md:hidden">
              {data.members.map((member) => (
                <div
                  key={member.id}
                  className="rounded-lg border p-4"
                  data-testid={`member-card-${member.id}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate font-medium">{member.name}</span>
                      <span className="truncate text-sm text-muted-foreground">{member.email}</span>
                    </div>
                    <Badge variant="secondary" className="shrink-0">
                      {member.role === 'admin' ? t('team.role_admin') : t('team.role_member')}
                    </Badge>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <select
                      value={member.role}
                      aria-label={t('team.role_aria', { name: member.name })}
                      disabled={isLastAdmin(member)}
                      className="flex-1 rounded-md border bg-background px-2 py-1.5 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                      onChange={(e) => {
                        void handleRoleChange(member, e.target.value as Role);
                      }}
                    >
                      <option value="admin">{t('team.role_admin')}</option>
                      <option value="member">{t('team.role_member')}</option>
                    </select>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      disabled={isLastAdmin(member)}
                      className="min-h-11"
                      onClick={() => {
                        clearFeedback();
                        setRemoveTarget(member);
                      }}
                    >
                      {t('team.remove_button')}
                    </Button>
                  </div>
                  {isLastAdmin(member) && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {t('team.last_admin_hint')}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : null}
      </section>

      {/* Pending invitations section */}
      {data !== null && (
        <section aria-label={t('team.pending_title')}>
          <h2 className="mb-3 text-base font-semibold">{t('team.pending_title')}</h2>

          {data.pending_invitations.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('team.no_pending')}</p>
          ) : (
            <>
              {/* Desktop table */}
              <div data-testid="team-invitations-desktop" className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('team.email_label')}</TableHead>
                      <TableHead>{t('team.role_label')}</TableHead>
                      <TableHead className="text-right">{''}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.pending_invitations.map((inv) => (
                      <TableRow key={inv.id}>
                        <TableCell className="font-medium">
                          <span className="block max-w-64 truncate">{inv.email}</span>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{t('team.status_pending')}</Badge>
                          <span className="ml-2 text-sm text-muted-foreground">
                            {inv.role === 'admin' ? t('team.role_admin') : t('team.role_member')}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            aria-label={t('team.revoke_aria', { email: inv.email })}
                            data-testid={`invite-revoke-${inv.id}`}
                            className="min-h-11"
                            onClick={() => {
                              clearFeedback();
                              setRevokeTarget(inv);
                            }}
                          >
                            {t('team.revoke_button')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile cards */}
              <div data-testid="team-invitations-mobile" className="flex flex-col gap-3 md:hidden">
                {data.pending_invitations.map((inv) => (
                  <div key={inv.id} className="rounded-lg border p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-col">
                        <span className="truncate font-medium">{inv.email}</span>
                        <span className="text-sm text-muted-foreground">
                          {inv.role === 'admin' ? t('team.role_admin') : t('team.role_member')}
                        </span>
                      </div>
                      <Badge variant="outline" className="shrink-0">
                        {t('team.status_pending')}
                      </Badge>
                    </div>
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="min-h-11"
                        onClick={() => {
                          clearFeedback();
                          setRevokeTarget(inv);
                        }}
                      >
                        {t('team.revoke_button')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>
      )}

      <InviteDialog
        open={isInviteOpen}
        onOpenChange={setInviteOpen}
        onInvited={async (email) => {
          setInviteOpen(false);
          clearFeedback();
          setActionNotice(t('team.invite_success', { email }));
          await fetchData();
        }}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('team.confirm_remove_title')}
        description={t('team.confirm_remove_body')}
        confirmLabel={t('team.remove_button')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleRemoveConfirm}
      />

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(open) => !open && setRevokeTarget(null)}
        title={t('team.confirm_revoke_title')}
        description={t('team.confirm_revoke_body')}
        confirmLabel={t('team.revoke_button')}
        cancelLabel={t('common.cancel')}
        onConfirm={handleRevokeConfirm}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Invite dialog — react-hook-form + zod (F066 #36). API errors stay inline in
// the dialog (near their origin) instead of leaking into the page banner.
// ---------------------------------------------------------------------------

const inviteSchema = z.object({
  email: z.string().min(1, 'team.form_email_required').email('team.form_email_invalid'),
  role: z.enum(['admin', 'member']),
});

type InviteFormValues = z.infer<typeof inviteSchema>;

interface InviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInvited: (email: string) => void | Promise<void>;
}

function InviteDialog({ open, onOpenChange, onInvited }: InviteDialogProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [apiError, setApiError] = useState<string | null>(null);
  const [draftRestored, setDraftRestored] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<InviteFormValues>({
    resolver: zodResolver(inviteSchema),
    defaultValues: { email: '', role: 'member' },
  });

  // F100/A12 — recover an in-progress invite if the session expired mid-form.
  const draft = useDraft<InviteFormValues>(`team-invite:${user?.id ?? 'anon'}`);

  useEffect(() => {
    if (open) {
      const recovered = draft.restore();
      if (recovered && (recovered.email || recovered.role !== 'member')) {
        reset({ email: recovered.email ?? '', role: recovered.role ?? 'member' });
        setDraftRestored(true);
      }
    } else {
      reset();
      setApiError(null);
      setDraftRestored(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  // Persist (debounced) as the owner types; clear when the form is back to its
  // empty default so no phantom draft survives.
  useEffect(() => {
    if (!open) return;
    const sub = watch((values) => {
      if (values.email || values.role !== 'member') {
        draft.save({ email: values.email ?? '', role: values.role ?? 'member' });
      } else {
        draft.clear();
      }
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, watch]);

  const onSubmit = async (values: InviteFormValues) => {
    setApiError(null);
    try {
      await teamService.invite({ email: values.email, role: values.role });
      draft.clear(); // F100 — invited, drop the recovery buffer
      await onInvited(values.email);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'member_exists') {
        setApiError(t('team.error_member_exists'));
      } else {
        setApiError(t('team.error_generic'));
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
          <DialogHeader>
            <DialogTitle>{t('team.invite_button')}</DialogTitle>
            <DialogDescription>{t('team.invite_subtitle')}</DialogDescription>
          </DialogHeader>
          {draftRestored ? (
            <p
              role="status"
              data-testid="invite-draft-restored"
              className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
            >
              {t('common.draft_restored')}
            </p>
          ) : null}
          <div className="flex flex-col gap-3 py-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="invite-email">{t('team.form_email_label')}</Label>
              <Input
                id="invite-email"
                data-testid="invite-email-input"
                type="email"
                placeholder={t('team.form_email_placeholder')}
                aria-invalid={errors.email ? true : undefined}
                className="h-11"
                {...register('email')}
              />
              {errors.email?.message && (
                <p className="text-sm text-destructive" role="alert">
                  {t(errors.email.message)}
                </p>
              )}
            </div>
            <select
              data-testid="invite-role-select"
              className="h-11 rounded-lg border bg-background px-3 text-sm"
              {...register('role')}
            >
              <option value="member">{t('team.role_member')}</option>
              <option value="admin">{t('team.role_admin')}</option>
            </select>
            {apiError && (
              <p className="text-sm text-destructive" role="alert">
                {apiError}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" data-testid="invite-submit" disabled={isSubmitting}>
              {t('team.form_submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
